import "./styles/global/core.scss";
import { createApp, nextTick } from "vue";
import { createPinia } from "pinia";
import { MdsConfig } from "@maersk-global/mds-config";
import App from "@/app/App.vue";
import { instantiateRouter } from "./router";
import { authService, userAnalyticsService } from "@/services";
import i18n from "@/i18n";
import { bypassAuth } from "./global";
import { useUserStore } from "@/store/user/user";
import {
  FEATURE_NAMES,
  logInfo,
  logError,
  registerGlobalErrorHandlers,
  reportClientError,
  CLIENT_ERROR_CATEGORY
} from "@/loggers";
import { registerDirectives } from "@/directives";

// icons are copied to assets during building, making them available from root
// during development they are resolved from node_modules, similar to vite-maersk-template but without using ../../

MdsConfig.iconsDynamicImportPath = import.meta.env.DEV && !import.meta.env.VITE_E2E ? "/node_modules/" : "./";

(async () => {
  // Capture uncaught runtime errors as early as possible, before any app code runs.
  registerGlobalErrorHandlers();

  logInfo(FEATURE_NAMES.MVS, `App initialization started - URL: ${location.href}`);

  if (!bypassAuth && authService.isAuthenticationFlowRedirect(location.search)) {
    logInfo(FEATURE_NAMES.MVS, `IAM redirect detected - processing callback from: ${location.href}`);
    try {
      const url = await authService.processRedirect(location.href);
      window.history.replaceState({}, "", url); // strip the auth flow from the url
      logInfo(FEATURE_NAMES.MVS, `IAM redirect processed successfully - final URL: ${url}`);
    } catch (error) {
      logError(FEATURE_NAMES.MVS, `IAM redirect processing failed: ${error}`);
    }
  } else {
    logInfo(FEATURE_NAMES.MVS, `No IAM redirect detected - normal app load`);
  }

  const router = instantiateRouter(authService);

  const app = createApp(App);

  // Vue swallows component/lifecycle errors before they reach window.onerror,
  // so route them through the same client-error pipeline (and keep dev visibility).
  app.config.errorHandler = (err, _instance, info) => {
    reportClientError(err, CLIENT_ERROR_CATEGORY.UNCAUGHT_JS, `vue:${info}`);
    if (import.meta.env.DEV) {
      console.error(err);
    }
  };

  app.use(createPinia());
  app.use(router); // router is instantiated late to avoid url processing during auth flow
  app.use(i18n);

  // Register custom directives
  registerDirectives(app);
  // Initialize auth if bypassing
  if (bypassAuth) {
    console.log("Initializing mock authentication");
    await authService.login();
    console.log("Mock authentication completed");
  }

  await router.isReady();
  app.mount("#maersk-app");

  nextTick(() => {
    document.getElementById("app-loading")?.remove();

    if ("requestIdleCallback" in window) {
      requestIdleCallback(() => {
        userAnalyticsService.injectExternalScripts();
        const userStore = useUserStore();
        userAnalyticsService.injectPlausibleScript(
          userStore.isUserInternal,
          userStore.customerCode,
          i18n.global.locale.value
        );
      });
    } else {
      // Fallback for unsupported browsers
      setTimeout(() => {
        userAnalyticsService.injectExternalScripts();
        const userStore = useUserStore();
        userAnalyticsService.injectPlausibleScript(
          userStore.isUserInternal,
          userStore.customerCode,
          i18n.global.locale.value
        );
      }, 200);
    }
  });
})();
